import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Campaign as CallIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  CourierCall,
  CourierCallAnswer,
  CourierPanelRow,
  courierModuleService,
  isCallable,
  toApiSource,
} from './courierModuleService';

interface Props {
  open: boolean;
  branchId?: string | null;
  /** Çağrının bağlı olduğu sipariş (yoksa "şubeye kurye lazım" çağrısı). */
  order?: any | null;
  /** Bu siparişin açık çağrısı ya da listeden açılan çağrı. */
  call?: CourierCall | null;
  rows: CourierPanelRow[];
  onClose: () => void;
  /** Çağrı açıldı / iptal edildi: panel çağrıları yeniden okur. */
  onChanged: () => void;
}

const minutesAgo = (iso?: string | null): number => {
  if (!iso) return 0;
  const ms = Date.now() - new Date(iso.endsWith('Z') ? iso : `${iso}Z`).getTime();
  return ms > 0 ? Math.round(ms / 60000) : 0;
};

/** "Kurye çağır": uygulamadaki kuryelere bildirim; cevaplar canlı görünür. */
const CourierCallDialog: React.FC<Props> = ({ open, branchId, order, call, rows, onClose, onChanged }) => {
  const { t } = useTranslation('salesSystem');
  const callable = useMemo(() => rows.filter(isCallable), [rows]);
  const [picked, setPicked] = useState<number[]>([]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Açık çağrı varken "Yeniden çağır" seçim ekranına döner
  const [compose, setCompose] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicked(callable.map((r) => r.id));
    setMessage('');
    setError(null);
    setCompose(!call);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, call?.id]);

  const orderLabel = call?.orderNumber || order?.shortOrderNo || order?.id;
  const title = orderLabel
    ? `${t('courierModule.panel.calls.title')} · #${orderLabel}`
    : t('courierModule.panel.calls.title');

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      await courierModuleService.createCall({
        branchId: branchId || call?.branchId || null,
        orderSource: order ? toApiSource(order.source) : call?.orderSource ?? null,
        orderRef: order ? Number(order.id) : call?.orderRef ?? null,
        orderNumber: order ? String(order.shortOrderNo || order.id) : call?.orderNumber ?? null,
        courierIds: picked.length === callable.length ? null : picked,
        message: message.trim() || null,
      });
      onChanged();
      onClose();
    } catch (e: any) {
      setError(e?.message || t('courierModule.panel.calls.failed'));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!call) return;
    setBusy(true);
    setError(null);
    try {
      await courierModuleService.cancelCall(call.id);
      onChanged();
      onClose();
    } catch (e: any) {
      setError(e?.message || t('courierModule.panel.calls.failed'));
    } finally {
      setBusy(false);
    }
  };

  const stateText = (r?: CourierPanelRow) => {
    if (!r) return '—';
    return r.state === 'on_road'
      ? t('courierModule.panel.onRoad', { count: r.inHandCount })
      : t('courierModule.panel.available');
  };

  const answerChip = (a?: CourierCallAnswer) => {
    if (!a || a.answer === 'pending') {
      return <Chip size="small" variant="outlined" label={t('courierModule.panel.calls.sent')} />;
    }
    if (a.answer === 'coming') {
      const accepted = call?.acceptedCourierId === a.courierId;
      return (
        <Chip
          size="small"
          color={accepted ? 'success' : 'default'}
          label={a.etaMinutes
            ? t('courierModule.panel.calls.comingEta', { minutes: a.etaMinutes })
            : t('courierModule.panel.calls.coming')}
        />
      );
    }
    return <Chip size="small" color="warning" variant="outlined" label={t('courierModule.panel.calls.declined')} />;
  };

  const kindLabel = (r?: CourierPanelRow) => (r ? t(`courierModule.kinds.${r.kind}`) : '');

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CallIcon color="primary" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} noWrap>{title}</Typography>
          {call && !compose && (
            <Typography variant="caption" color="text.secondary">
              {t('courierModule.panel.calls.sentAgo', { minutes: minutesAgo(call.createdAt) })}
              {call.createdBy ? ` · ${call.createdBy}` : ''}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1}>
            <Chip color="primary" label={t('courierModule.panel.calls.appCouriers')} />
            <Chip variant="outlined" disabled label={t('courierModule.panel.calls.fiyuuLater')} />
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}

          {compose ? (
            callable.length === 0 ? (
              <Alert severity="info">{t('courierModule.panel.calls.noAppCouriers')}</Alert>
            ) : (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={picked.length === callable.length}
                          indeterminate={picked.length > 0 && picked.length < callable.length}
                          onChange={(e) => setPicked(e.target.checked ? callable.map((r) => r.id) : [])}
                        />
                      </TableCell>
                      <TableCell>{t('courierModule.panel.courier')}</TableCell>
                      <TableCell>{t('courierModule.panel.cols.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {callable.map((r) => {
                      const on = picked.includes(r.id);
                      return (
                        <TableRow key={r.id} hover onClick={() => setPicked((p) => (on ? p.filter((x) => x !== r.id) : [...p, r.id]))} sx={{ cursor: 'pointer' }}>
                          <TableCell padding="checkbox"><Checkbox size="small" checked={on} /></TableCell>
                          <TableCell>
                            <Typography fontWeight={700} fontSize={13} component="span">{r.name}</Typography>{' '}
                            <Chip size="small" sx={{ height: 18, fontSize: 10 }} variant="outlined" label={kindLabel(r)} />
                          </TableCell>
                          <TableCell><Typography variant="caption" color="text.secondary">{stateText(r)}</Typography></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <TextField
                  size="small"
                  label={t('courierModule.panel.calls.message')}
                  placeholder={t('courierModule.panel.calls.messageHint')}
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, 200))}
                />
              </>
            )
          ) : call ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('courierModule.panel.courier')}</TableCell>
                  <TableCell>{t('courierModule.panel.cols.status')}</TableCell>
                  <TableCell>{t('courierModule.panel.calls.answer')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {call.answers.map((a) => {
                  const r = rows.find((x) => x.id === a.courierId);
                  return (
                    <TableRow key={a.courierId}>
                      <TableCell>
                        <Typography fontWeight={700} fontSize={13} component="span">{a.courierName || r?.name}</Typography>{' '}
                        {r && <Chip size="small" sx={{ height: 18, fontSize: 10 }} variant="outlined" label={kindLabel(r)} />}
                      </TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary">{stateText(r)}</Typography></TableCell>
                      <TableCell>{answerChip(a)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : null}

          <Alert severity="info" sx={{ py: 0.25 }}>{t('courierModule.panel.calls.info')}</Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        {call && !compose && (
          <>
            <Button color="error" onClick={cancel} disabled={busy}>{t('courierModule.panel.calls.cancel')}</Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={() => setCompose(true)} disabled={busy}>{t('courierModule.panel.calls.again')}</Button>
          </>
        )}
        <Button onClick={onClose} disabled={busy}>{t('courierModule.panel.calls.close')}</Button>
        {compose && (
          <Button
            variant="contained"
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <CallIcon />}
            disabled={busy || picked.length === 0}
            onClick={send}
          >
            {t('courierModule.panel.calls.send', { count: picked.length })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CourierCallDialog;
